import React, { lazy, Suspense } from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { LanguageProvider } from './context/LanguageContext';
import { HelmetProvider } from 'react-helmet-async';
import WhatsAppButton from './components/WhatsAppButton';
import StarsBackground from './components/StarsBackground';

// Skeleton Loader for route transitions
const PageSkeleton = () => (
  <div className="min-h-screen bg-transparent animate-pulse">
    <div className="h-16 bg-[#1E293B]/30 border-b border-white/5" />
    <div className="max-w-7xl mx-auto px-4 pt-20">
      <div className="h-8 w-64 bg-[#1E293B]/40 rounded-lg mb-4" />
      <div className="h-4 w-96 bg-[#1E293B]/30 rounded mb-8" />
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="h-48 bg-[#1E293B]/30 rounded-xl" />
        <div className="h-48 bg-[#1E293B]/30 rounded-xl" />
        <div className="h-48 bg-[#1E293B]/30 rounded-xl" />
      </div>
    </div>
  </div>
);

// ========== LAZY LOADED PUBLIC PAGES ==========
const HomePage = lazy(() => import('./pages/HomePage'));
const ProductDetailPage = lazy(() => import('./pages/ProductDetailPage'));
const CorporateSalesPage = lazy(() => import('./pages/CorporateSalesPage'));
const FreeSamplePage = lazy(() => import('./pages/FreeSamplePage'));
const BlogPage = lazy(() => import('./pages/BlogPage'));
const BlogDetailPage = lazy(() => import('./pages/BlogDetailPage'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const TrackingPage = lazy(() => import('./pages/TrackingPage'));
const SSSPage = lazy(() => import('./pages/SSSPage'));
const ShopPage = lazy(() => import('./pages/ShopPage'));
const OtelLandingPage = lazy(() => import('./pages/OtelLandingPage'));
const SurveyPage = lazy(() => import('./pages/SurveyPage'));

// ========== LAZY LOADED ADMIN PAGES ==========
const AdminLogin = lazy(() => import('./pages/admin/AdminLogin'));
const AdminLayout = lazy(() => import('./pages/admin/AdminLayout'));
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'));
const AdminBlogList = lazy(() => import('./pages/admin/AdminBlogList'));
const AdminBlogEdit = lazy(() => import('./pages/admin/AdminBlogEdit'));
const AdminSamples = lazy(() => import('./pages/admin/AdminSamples'));
const AdminReviews = lazy(() => import('./pages/admin/AdminReviews'));
const AdminSales = lazy(() => import('./pages/admin/AdminSales'));
const AdminContacts = lazy(() => import('./pages/admin/AdminContacts'));
const AdminUsers = lazy(() => import('./pages/admin/AdminUsers'));
const AdminHotels = lazy(() => import('./pages/admin/AdminHotels'));
const AdminB2BProducts = lazy(() => import('./pages/admin/AdminB2BProducts'));
const AdminCustomers = lazy(() => import('./pages/admin/AdminCustomers'));
const AdminOrders = lazy(() => import('./pages/admin/AdminOrders'));
const AdminSubscriptions = lazy(() => import('./pages/admin/AdminSubscriptions'));
const AdminLoyalty = lazy(() => import('./pages/admin/AdminLoyalty'));
const AdminWhatsApp = lazy(() => import('./pages/admin/AdminWhatsApp'));
const AdminSuppliers = lazy(() => import('./pages/admin/AdminSuppliers'));
const AdminFAQ = lazy(() => import('./pages/admin/AdminFAQ'));
const AdminConsumption = lazy(() => import('./pages/admin/AdminConsumption'));
const AdminQuotes = lazy(() => import('./pages/admin/AdminQuotes'));
const AdminConsultants = lazy(() => import('./pages/admin/AdminConsultants'));
const AdminCommissions = lazy(() => import('./pages/admin/AdminCommissions'));
const AdminDealers = lazy(() => import('./pages/admin/AdminDealers'));
const AdminDealerOrders = lazy(() => import('./pages/admin/AdminDealerOrders'));
const AdminDealerInvoices = lazy(() => import('./pages/admin/AdminDealerInvoices'));
const AdminDealerPayments = lazy(() => import('./pages/admin/AdminDealerPayments'));
const AdminDealerLedger = lazy(() => import('./pages/admin/AdminDealerLedger'));
const ConsultantDashboardPage = lazy(() => import('./pages/ConsultantDashboardPage'));
const BayiDashboardPage = lazy(() => import('./pages/BayiDashboardPage'));
const BayiLoginPage = lazy(() => import('./pages/bayi/BayiLoginPage'));
const DealerApplicationPage = lazy(() => import('./pages/bayi/DealerApplicationPage'));
const AdminDealerApplications = lazy(() => import('./pages/admin/AdminDealerApplications'));
const AdminLeads = lazy(() => import('./pages/admin/AdminLeads'));
const AdminLeadsPipeline = lazy(() => import('./pages/admin/AdminLeadsPipeline'));
const AdminMeetings = lazy(() => import('./pages/admin/AdminMeetings'));
const AdminCommissionPage = lazy(() => import('./pages/admin/AdminCommissionPage'));
const SalesCommissionPage = lazy(() => import('./pages/admin/SalesCommissionPage'));
const AdminReminders = lazy(() => import('./pages/admin/AdminReminders'));
const HelpPage = lazy(() => import('./pages/HelpPage'));
const ConsultantGuidePage = lazy(() => import('./pages/ConsultantGuidePage'));
const PublicQuotePage = lazy(() => import('./pages/PublicQuotePage'));

// Admin Route Protection
const ProtectedRoute = ({ children }) => {
  const token = localStorage.getItem('token');
  const legacyAuth = localStorage.getItem('adminAuth');
  if (!token && !legacyAuth) {
    return <Navigate to="/admin/login" replace />;
  }
  return children;
};

// Show WhatsApp button only on public pages
const WhatsAppWrapper = () => {
  const location = useLocation();
  if (location.pathname.startsWith('/admin')) return null;
  return <WhatsAppButton />;
};

function App() {
  return (
    <HelmetProvider>
    <LanguageProvider>
      <div className="App">
        <BrowserRouter>
          <StarsBackground />
          <WhatsAppWrapper />
          <Suspense fallback={<PageSkeleton />}>
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<HomePage />} />
            <Route path="/product/:slug" element={<ProductDetailPage />} />
            <Route path="/corporate-sales" element={<CorporateSalesPage />} />
            <Route path="/free-sample" element={<FreeSamplePage />} />
            <Route path="/blog" element={<BlogPage />} />
            <Route path="/blog/:slug" element={<BlogDetailPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/track" element={<TrackingPage />} />
            <Route path="/shop" element={<ShopPage />} />
            <Route path="/sss" element={<SSSPage />} />
            <Route path="/otel" element={<OtelLandingPage />} />
            <Route path="/survey/:sampleId" element={<SurveyPage />} />
            <Route path="/teklif/:token" element={<PublicQuotePage />} />
            <Route path="/bayilik-basvuru" element={<DealerApplicationPage />} />
            <Route path="/bayilik" element={<Navigate to="/partner" replace />} />
            <Route path="/faq" element={<Navigate to="/sss" replace />} />

            {/* Admin Routes */}
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/yardim" element={<HelpPage />} />
            <Route path="/help" element={<HelpPage />} />
            <Route path="/danisman-kilavuz" element={<ConsultantGuidePage />} />
            <Route path="/consultant-guide" element={<ConsultantGuidePage />} />
            <Route path="/partner" element={<BayiLoginPage />} />
            <Route path="/partner/login" element={<Navigate to="/partner" replace />} />
            <Route path="/bayi/login" element={<Navigate to="/partner" replace />} />
            <Route
              path="/consultant/dashboard"
              element={
                <ProtectedRoute>
                  <ConsultantDashboardPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/bayi/dashboard"
              element={
                <ProtectedRoute>
                  <BayiDashboardPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin"
              element={
                <ProtectedRoute>
                  <AdminLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<AdminDashboard />} />
              <Route path="users" element={<AdminUsers />} />
              <Route path="hotels" element={<AdminHotels />} />
              <Route path="blog" element={<AdminBlogList />} />
              <Route path="blog/new" element={<AdminBlogEdit />} />
              <Route path="blog/edit/:id" element={<AdminBlogEdit />} />
              <Route path="samples" element={<AdminSamples />} />
              <Route path="reviews" element={<AdminReviews />} />
              <Route path="sales" element={<AdminSales />} />
              <Route path="contacts" element={<AdminContacts />} />
              <Route path="b2b-products" element={<AdminB2BProducts />} />
              <Route path="customers" element={<AdminCustomers />} />
              <Route path="orders" element={<AdminOrders />} />
              <Route path="quotes" element={<AdminQuotes />} />
              <Route path="consultants" element={<AdminConsultants />} />
              <Route path="commissions" element={<AdminCommissions />} />
              <Route path="dealers" element={<AdminDealers />} />
              <Route path="dealer-orders" element={<AdminDealerOrders />} />
              <Route path="dealer-invoices" element={<AdminDealerInvoices />} />
              <Route path="dealer-payments" element={<AdminDealerPayments />} />
              <Route path="dealer-ledger" element={<AdminDealerLedger />} />
              <Route path="dealer-applications" element={<AdminDealerApplications />} />
              <Route path="suppliers" element={<AdminSuppliers />} />
              <Route path="subscriptions" element={<AdminSubscriptions />} />
              <Route path="loyalty" element={<AdminLoyalty />} />
              <Route path="whatsapp" element={<AdminWhatsApp />} />
              <Route path="faq" element={<AdminFAQ />} />
              <Route path="consumption" element={<AdminConsumption />} />
              <Route path="leads" element={<AdminLeads />} />
              <Route path="leads/pipeline" element={<AdminLeadsPipeline />} />
              <Route path="meetings" element={<AdminMeetings />} />
              <Route path="prim" element={<AdminCommissionPage />} />
              <Route path="benim-primim" element={<SalesCommissionPage />} />
              <Route path="reminders" element={<AdminReminders />} />
            </Route>
          </Routes>
          </Suspense>
        </BrowserRouter>
      </div>
    </LanguageProvider>
    </HelmetProvider>
  );
}

export default App;
